import React, { useMemo, useEffect, useState } from 'react';
import { PromiseState, connect } from 'react-refetch';
import { match, Redirect, Route, useHistory } from 'react-router';
import { matchPath } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Location } from 'history';
import isEqual from 'lodash.isequal';

import { loadRecentHeaders, saveToApi } from 'redux/reducers/user/browseTable';

import getCustomListUrl from 'url-constructors/getCustomListUrl';
import getCustomListInvitationURL from 'url-constructors/getCustomListInvitationURL';
import useComponentSettings from 'hooks/useComponentSettings';
import useSortingWithEffects from 'components/data-containers/BrowseEntities/shared/useSortingWithEffects';
import getBrowseCustomEntityListUrl from 'url-constructors/getBrowseCustomEntityListUrl';
import getInitialSalesType from 'helpers/getInitialSalesType';
import getSchema from 'schemas/componentSettings/browse';
import useMedia from 'hooks/useMedia';
import useListCustomerFilters from './hooks/useListCustomerFilters';

import {
  selectUserId,
  checkIsExternalOneLabelUser,
} from 'redux/reducers/authentication';
import { shouldLoadLists, loadMyLists } from 'redux/reducers/myLists';

import salesTypes, { SalesTypeValues } from 'maps/sales-types';
import { sortOrderTypes } from 'types/shared';

import ErrorPage from 'pages/ErrorPage';
import Content from 'components/customlists/Content';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import CustomListPageHeader from 'components/customlists/CustomListPageHeader';
import NotificationSettingsModal from '../../components/notifications/Settings';
import ViewList from './ViewList';
import EditList from './EditList';
import { IBrowseCustomList } from './shared';
import { isUserCuratorForList } from '../../components/customlists/utils/isUserCuratorForList';
import { TAsyncDispatchResult } from 'types/shared';
import {
  ICustomList,
  ICustomListData,
} from 'components/customlists/AddToList/shared';
import TopicFilters from '../../components/TopicFilters/TopicFilters';
import getSortByValue from './helpers/getSortByValue';
import { TopicFiltersWrap, CustomListRouteWrap } from './CustomList.style';
import { TTableHeader } from 'components/CustomizeView/CustomizeView';
import {
  loadCustomRegions,
  shouldLoadCustomRegions,
} from 'redux/reducers/customRegions';
import { RootState } from 'redux/rootReducer';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

interface IOptions {
  salesType: SalesTypeValues;
  customerFilter: string[];
  nameFilter?: string;
  sortby?: string;
  sortOrder?: string;
  limit: number;
  offset: number;
}

function dataSources(props: IOuterProps): any {
  const listId = props.match.params.id;
  const {
    topicFilters: { country, media, region, customRegion },
  } = props;

  return {
    customListMetadataFetch: (force = false): any => {
      return {
        customListMetadata: {
          url: getCustomListUrl({
            listId,
            expand: 'permissions',
          }),
          force: force,
          refreshing: force,
        },
      };
    },
    listFetch: (limit: number, offset: number): any => ({
      browseListFetchData: {
        url: getBrowseCustomEntityListUrl({ listId, limit, offset }),
      },
      browseListFetchAllData: {
        url: getBrowseCustomEntityListUrl({ listId }),
      },
    }),
    browseListFetch: ({
      salesType,
      customerFilter,
      nameFilter,
      sortby,
      sortOrder,
      limit,
      offset,
    }: IOptions): any => {
      return {
        browseListData: {
          url: getBrowseCustomEntityListUrl({
            salesType,
            listId,
            countryIds: country,
            customerFilter,
            nameFilter,
            sortby,
            sortOrder,
            limit,
            offset,
            region,
            media: salesType !== 'physical' ? media : undefined,
            customRegion,
          }),
        },
      };
    },
    updateList: (
      list: IBrowseCustomList,
      limit: number,
      offset: number,
      parameters: IOptions,
      next: () => void
    ): any => {
      return {
        browseListFetchData: {
          url: `/api/customEntityLists/${list.id}`,
          method: 'PUT',
          body: JSON.stringify(list),
          andThen: (): any => ({
            browseListFetchData: {
              url: getBrowseCustomEntityListUrl({
                listId: list.id,
                limit,
                offset,
              }),
              // `react-refetch` compares request arguments
              // and sends back a cached response when unchanged.
              // We never change arguments so `force` is required.
              force: true,
              refreshing: true,
            },
            browseListData: {
              url: getBrowseCustomEntityListUrl({
                listId: list.id,
                ...parameters,
                countryId: country,
              }),
              force: true,
              refreshing: true,
              then: next,
            },
          }),
        },
      };
    },
    putCustomEntityList(list: ICustomListData, close: () => void): any {
      return {
        customListMetadata: {
          url: `/api/customEntityLists/${list.id}`,
          method: 'PUT',
          body: JSON.stringify(list),
          // close dialog
          then: (): any => {
            close();
            return getCustomListUrl({
              listId,
              expand: 'permissions',
            });
          },
        },
      };
    },
    deleteCustomEntityList(listId: string, next: () => void): any {
      return {
        customListMetadata: {
          url: `/api/customEntityLists/${listId}`,
          method: 'DELETE',
          then: (): void => {
            // move to next screen
            next();
          },
        },
      };
    },
    leaveCustomEntityList: (
      listId: string,
      userId: string,
      close: () => void
    ): any => {
      return {
        customListMetadata: {
          url: `/api/customEntityLists/${listId}/permissions`,
          method: 'DELETE',
          body: JSON.stringify([userId]),
          // close dialog
          then: (): any => {
            close();
            return {
              url: getCustomListUrl({
                listId,
                expand: 'permissions',
              }),
            };
          },
        },
      };
    },
    fetchCustomListInvitation: ({ listId }: { listId: string }): any => {
      return {
        customListInvitationData: {
          url: getCustomListInvitationURL({ listId }),
          method: 'POST',
          force: true,
          body: JSON.stringify({
            ttl: 864000, // 10 days
            roles: ['admin'],
          }),
        },
      };
    },
  };
}

interface ICustomListMatchParams {
  id: string;
}

interface ITopicFilters {
  country: string;
  region: string;
  media: string;
  streaming: string;
  download: string;
  customRegion: string | string[];
}
interface IOuterProps {
  match: match<ICustomListMatchParams>;
  topicFilters: ITopicFilters;
  location: Location;
}

interface ICustomListProps extends IOuterProps {
  customListMetadataFetch: (force?: boolean) => void;
  customListMetadata: PromiseState<ICustomList>;
  fetchBrowseList: (options: any) => any;
  browseListData: PromiseState<IBrowseCustomList>;
  updateList: (
    list: IBrowseCustomList,
    limit: number,
    offset: number,
    parameters: IOptions,
    next: () => void
  ) => any;
  browseListFetch: ({ salesType, customerFilter }: IOptions) => any;
  putCustomEntityList: (list: ICustomListData, close: () => void) => any;
  deleteCustomEntityList: (listId: string, next: () => void) => any;
  leaveCustomEntityList: (
    listId: string,
    userId: string,
    close: () => void
  ) => any;
  fetchCustomListInvitation: ({ listId }: { listId: string }) => any;
  customListInvitationData: PromiseState<any>;
  listFetch: (limit: number, offset: number) => void;
  browseListFetchData: PromiseState<any>;
  browseListFetchAllData: PromiseState<any>;
  topicFilters: ITopicFilters;
}

const CustomList: React.FC<ICustomListProps> = (props) => {
  const {
    browseListData = PromiseState.create<IBrowseCustomList>(),
    browseListFetch,
    customListInvitationData = PromiseState.create(),
    customListMetadata = PromiseState.create<ICustomList>(),
    customListMetadataFetch,
    deleteCustomEntityList,
    leaveCustomEntityList,
    location,
    fetchCustomListInvitation,
    putCustomEntityList,
    updateList,
    listFetch,
    browseListFetchData,
    browseListFetchAllData,
    topicFilters,
  } = props;

  const isExternalUser = useSelector(checkIsExternalOneLabelUser);
  const initialSalesType = getInitialSalesType(isExternalUser);
  const browseSchema = getSchema(initialSalesType);
  const settingsId = 'browse-custom-lists';
  const [componentSettings, saveComponentSettings] = useComponentSettings(
    settingsId,
    browseSchema
  );
  const { selectedSalesType: sales } = componentSettings;
  const selectedSalesType = salesTypes[sales];
  const customSort = {
    key: 'custom',
    label: 'Custom',
    sortOrder: 'desc',
    value: 'custom',
  };
  const initialSortMetric = customSort;
  const [nameFilter, setNameFilter] = useState('');
  const [limit, setLimit] = useState(100);
  const [offsetState, setOffsetState] = useState(0);

  const [allowedCustomerFilters, customerFilter] = useListCustomerFilters(
    selectedSalesType.value
  );
  const [topicFiltersState, saveCurrentTopicFilters] = useState(topicFilters);

  const dispatch = useDispatch();
  const history = useHistory();
  useEffect(() => {
    if (dispatch(shouldLoadLists())) {
      dispatch(loadMyLists());
    }
  }, [dispatch]);

  useEffect(() => {
    customListMetadataFetch();
  }, [customListMetadataFetch]);

  useEffect(() => {
    if (dispatch(shouldLoadCustomRegions())) {
      dispatch(loadCustomRegions());
    }
  }, [dispatch]);

  const customRegionState = useSelector(
    (state: RootState) => state.customRegions
  );

  const {
    sortOrder,
    setSortOrder,
    sortMetric,
    setSortMetric,
    switchSortOrder,
    changeSortMetric,
  } = useSortingWithEffects({
    initialSortMetric,
    apiType: '',
    browseEntityType: 'item name',
    tableHeadersType: 'standard',
  });

  useEffect(() => {
    browseListFetch({
      salesType: selectedSalesType.value,
      customerFilter,
      nameFilter,
      sortby: sortMetric
        ? getSortByValue(sortMetric?.value as string)
        : sortMetric,
      sortOrder:
        (sortMetric?.sortOrder as sortOrderTypes) ||
        (sortOrder as sortOrderTypes),
      limit,
      offset: offsetState,
    });
  }, [
    browseListFetch,
    selectedSalesType,
    customerFilter,
    nameFilter,
    sortMetric,
    sortOrder,
    limit,
    offsetState,
    customRegionState.fulfilled,
    customRegionState.rejected,
    customRegionState.data,
  ]);

  useEffect(() => {
    dispatch(loadRecentHeaders('customList'));
  }, [dispatch]);

  useEffect(() => {
    listFetch(limit, offsetState);
  }, [listFetch, limit, offsetState]);

  useEffect(() => {
    saveCurrentTopicFilters(topicFilters);
  }, [topicFilters]);

  const customList = useMemo(() => {
    if (customListMetadata.fulfilled) {
      const customListData = customListMetadata.value;
      logToAmplitude('Custom List', {
        customListId: customListData.id,
        displayName: customListData.displayName,
      });
      return customListData;
    }
  }, [customListMetadata]);

  const isLargeScreen = useMedia('(min-width: 75rem)');
  const userId = useSelector(selectUserId);
  const isUserCurator = isUserCuratorForList({ customList, userId });
  const onSaveList = (list: IBrowseCustomList): void => {
    const parameters = {
      salesType: selectedSalesType.value,
      customerFilter,
      nameFilter,
      sortby: sortMetric
        ? getSortByValue(sortMetric?.value as string)
        : sortMetric,
      sortOrder:
        (sortMetric?.sortOrder as sortOrderTypes) ||
        (sortOrder as sortOrderTypes),
      limit,
      offset: offsetState,
      countryId: topicFilters.country,
    };
    updateList(list, limit, offsetState, parameters, () => {
      history.push(`/list/${list.id}`);
    });
  };
  if (customListMetadata.pending === false && !customListMetadata.value) {
    //[DF-6017] Visiting a invalid custom list url should show "something went wrong"
    return <ErrorPage error={new Error('Something went wrong')} status={404} />;
  }
  const isReadMode = matchPath(location.pathname, {
    path: `/list/${customList?.id}`,
  })?.isExact;

  const saveTableHeaders = (headers: TTableHeader[]): TAsyncDispatchResult =>
    dispatch(saveToApi(headers, 'customList'));

  return (
    <>
      <CustomListPageHeader
        customList={customList}
        customListInvitationData={customListInvitationData}
        customListModalMetadata={customListMetadata}
        deleteCustomEntityList={deleteCustomEntityList}
        fetchCustomListInvitation={fetchCustomListInvitation}
        leaveCustomEntityList={leaveCustomEntityList}
        loading={customListMetadata.pending}
        putCustomEntityList={putCustomEntityList}
        saveToApi={saveTableHeaders}
        userId={userId as string}
      />
      <CustomListRouteWrap isReadMode={isReadMode}>
        <NotificationSettingsModal />

        <Content>
          <TopicFiltersWrap>
            <TopicFilters
              topic="customList"
              selectedSalesType={selectedSalesType?.value}
            />
          </TopicFiltersWrap>
          <>
            <Route
              exact
              path={`/list/${customList?.id}/edit`}
              render={(renderProps: any): any => {
                if (isUserCurator) {
                  return (
                    <EditList
                      {...renderProps}
                      allowedFilters={allowedCustomerFilters}
                      list={browseListFetchData}
                      allLists={browseListFetchAllData}
                      onSave={onSaveList}
                      isPending={browseListData.pending}
                      isLargeScreen={isLargeScreen}
                      setLimit={setLimit}
                      setOffsetState={setOffsetState}
                      isSameFilters={isEqual(topicFilters, topicFiltersState)}
                      totalLimit={limit}
                    />
                  );
                }
                return <Redirect to={`/list/${customList?.id}`} />;
              }}
            />
            <Route
              exact
              path={`/list/${customList?.id}`}
              render={(renderProps: any): any => (
                <ViewList
                  {...renderProps}
                  allowedFilters={allowedCustomerFilters}
                  customListInvitationData={customListInvitationData}
                  customListMetadata={customListMetadata}
                  deleteCustomEntityList={deleteCustomEntityList}
                  fetchCustomListInvitation={fetchCustomListInvitation}
                  isLargeScreen={isLargeScreen}
                  isExternalUser={isExternalUser}
                  leaveCustomEntityList={leaveCustomEntityList}
                  list={browseListData}
                  putCustomEntityList={putCustomEntityList}
                  selectedSalesType={selectedSalesType}
                  setNameFilter={setNameFilter}
                  saveComponentSettings={saveComponentSettings}
                  sortMetric={sortMetric}
                  sortOrder={sortOrder}
                  setSortMetric={setSortMetric}
                  setSortOrder={setSortOrder}
                  changeSortMetric={changeSortMetric}
                  switchSortOrder={switchSortOrder}
                  setLimit={setLimit}
                  setOffsetState={setOffsetState}
                  saveToApi={saveTableHeaders}
                  userId={userId as string}
                  isSameFilters={isEqual(topicFilters, topicFiltersState)}
                  totalLimit={limit}
                  globalFilters={topicFilters}
                />
              )}
            />
          </>
        </Content>
      </CustomListRouteWrap>
      <BackToTop />
      <Footer />
    </>
  );
};

export default connect<ICustomListProps>(dataSources as any)(CustomList);
